import React from 'react';
import { getRidershipFilterCatalog } from '../../../actions/ridershipActions';
import type { RidershipFilterCatalogDirection } from '../../../types/ridershipTypes';
import type { DirectionCatalogOption } from '../average-weekday-by-stop/averageWeekdayByStopDirectionUtils';
import { buildDirectionOptionsFromCatalog,
    buildDirectionsLookupByRouteKey,
    getCatalogDirectionsForRouteKey } from './rankingDirectionUtils';

export interface RidershipDirectionCatalogFilters {
    fromDate: string;
    toDate: string;
}

interface UseRidershipDirectionCatalogResult {
    catalogLoading: boolean;
    getDirectionsForRoute: (routeId: string | null | undefined) => DirectionCatalogOption[];
}

export function useRidershipDirectionCatalog(
    agencyId: string | null | undefined,
    appliedFilters: RidershipDirectionCatalogFilters | null,
): UseRidershipDirectionCatalogResult {
    const [catalogLoading, setCatalogLoading] = React.useState(false);
    const [directionsByRouteKey, setDirectionsByRouteKey] = React.useState<Map<string, RidershipFilterCatalogDirection[]>>(new Map());

    React.useEffect(() => {
        if (!agencyId || !appliedFilters) {
            setDirectionsByRouteKey(new Map());
            setCatalogLoading(false);
            return;
        }

        let cancelled = false;
        setCatalogLoading(true);

        (async () => {
            try {
                const catalog = await getRidershipFilterCatalog(
                    agencyId,
                    appliedFilters.fromDate,
                    appliedFilters.toDate,
                );
                if (cancelled) {
                    return;
                }

                setDirectionsByRouteKey(buildDirectionsLookupByRouteKey(catalog.routes));
            } catch {
                if (!cancelled) {
                    setDirectionsByRouteKey(new Map());
                }
            } finally {
                if (!cancelled) {
                    setCatalogLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [agencyId, appliedFilters?.fromDate, appliedFilters?.toDate]);

    const getDirectionsForRoute = React.useCallback((routeId: string | null | undefined) => {
        if (!routeId) {
            return [];
        }

        const directions = getCatalogDirectionsForRouteKey(directionsByRouteKey, routeId);
        return buildDirectionOptionsFromCatalog(directions);
    }, [directionsByRouteKey]);

    return {
        catalogLoading,
        getDirectionsForRoute,
    };
}
